import imageCompression from "browser-image-compression";
import React, { useState } from "react";
import { Upload, Icon, Modal, message } from "antd";
import { platformApi } from "../../../../../../api";
import { ENDPOINT } from "../../../../../../constants";
import moment from "moment";

const uploadButton = (
  <div>
    <Icon type="plus" />
    <div className="ant-upload-text">Upload</div>
  </div>
);

const getBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const ImageUploader = ({
  type,
  fileList,
  setFileList,
  imageUrl,
  setImageUrl,
  editable,
  id,
  setLoading,
  onFileDelete, // New prop to notify parent about deletion
  onUploadSuccess, // New prop to notify parent about successful upload
}) => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const beforeUpload = async (file) => {
    const isImage = file.type.startsWith("image/");
    const isPDF = file.type === "application/pdf";

    if (isPDF && file.size / 1024 / 1024 > 2.5) {
      message.error("PDF size large!");
      return Upload.LIST_IGNORE;
    }

    if (isImage) {
      if (file.size / 1024 > 400) {
        try {
          const options = {
            maxSizeMB: 0.4,
            maxWidthOrHeight: 1920,
            initialQuality: 0.8,
            useWebWorker: true,
          };
          const compressedFile = await imageCompression(file, options);
          compressedFile.uid = file.uid;
          return compressedFile;
        } catch (err) {
          message.error("Image compression failed");
          return Upload.LIST_IGNORE;
        }
      }
    }

    return file;
  };

  const handleUpload = async ({ file, onProgress, onSuccess, onError }) => {
    console.log("DEBUG: handleUpload - Starting for:", file.name);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("profile", file);
      formData.append("master", "Transaction Master");
      formData.append("module", "vehicle");
      formData.append("id", id);
      formData.append("type", type.toLowerCase());

      const res = await platformApi.post("/api/upload/vehicle", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          onProgress({ percent: Math.round((event.loaded / event.total) * 100) });
        },
      });

      console.log("DEBUG: handleUpload - Success:", res.data);
      const { data } = res;
      if (data.code === 200) {
        const uploadedFile = data.response.data;
        setLoading(false);
        setImageUrl(uploadedFile.url);

        const newFile = {
          uid: uploadedFile.id,
          name: file.name,
          status: "done",
          url: uploadedFile.url,
          type: file.type,
          timestamp: uploadedFile.createdAt || moment().format(),
        };

        setFileList([newFile]);

        if (onUploadSuccess) {
          onUploadSuccess({
            id: uploadedFile.id,
            url: uploadedFile.url,
            type: type,
            timestamp: uploadedFile.createdAt || moment().format(),
          });
        }
        onSuccess(res.data, file);
        message.success("document uploaded successfully");
      } else {
        throw new Error(data.message || "Upload failed");
      }
    } catch (error) {
      console.error("DEBUG: handleUpload - Error:", error);
      setLoading(false);
      setFileList([]);
      onError(error);
      message.error("Upload failed");
    }
  };

  const handleChange = ({ fileList }) => {
    setFileList(fileList);
  };


  const handlePreview = async (file) => {
    console.log("DEBUG: ImageUploader handlePreview - File Object:", file);
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }

    if (file.type === "application/pdf" || file.name?.endsWith(".pdf")) {
      try {
        const targetUrl = file.url || file.preview;
        if (!targetUrl) throw new Error("No URL found");

        const response = await fetch(targetUrl);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPreviewFile({ ...file, url });
      } catch (err) {
        console.error("PDF Preview Error:", err);
        setPreviewFile(file);
      }
    } else {
      setPreviewFile(file);
    }
    setPreviewVisible(true);
  };

  const handleDelete = (file) => {
    if (!file || isDeleting) return;
    setIsDeleting(true);
    const delid = file.uid;
    const urlToDelete = file.url || imageUrl;

    platformApi
      .post("/api/upload/deleteFile", {
        delid,
        type,
        url: urlToDelete,
      })
      .then((res) => {
        if (res.data.code === 200) {
          // Clear local state
          setFileList([]);
          setImageUrl(null);

          // Notify parent about deletion
          if (onFileDelete && file.uid) {
            onFileDelete(file.uid);
          }

          message.success("File deleted successfully");
        } else {
          message.error("Delete failed");
        }
      })
      .catch((error) => {
        console.error("Delete error:", error);
        message.error("Unable to delete file");
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  return (
    <div>
      {imageUrl ? (
        // Already has an uploaded document — show it with optional delete
        <div
          style={{
            position: "relative",
            display: "inline-block",
            width: 200,
            height: 160,
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            overflow: "hidden",
            verticalAlign: "top",
          }}
        >
          {imageUrl.endsWith(".pdf") ? (
            <div
              onClick={() => window.open(imageUrl)}
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              📄 PDF File
            </div>
          ) : (
            <>
              <img
                src={imageUrl}
                alt={`${type} View`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
                onClick={() => {
                  setPreviewFile({
                    url: imageUrl,
                    type: "image",
                    timestamp: fileList[0]?.timestamp
                  });
                  setPreviewVisible(true);
                }}
              />
              {fileList[0]?.timestamp && (
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: "rgba(0,0,0,0.5)",
                    color: "white",
                    fontSize: "10px",
                    padding: "2px 5px",
                    textAlign: "right",
                    pointerEvents: "none",
                  }}
                >
                  {moment(fileList[0].timestamp).format("DD/MM/YYYY hh:mm A")}
                </div>
              )}
            </>
          )}
          {editable && (
            <Icon
              type="delete"
              style={{
                position: "absolute",
                top: 5,
                right: 5,
                color: "red",
                fontSize: "18px",
                background: "white",
                borderRadius: "50%",
                padding: "2px",
                cursor: "pointer",
                zIndex: 2,
              }}
              onClick={() => handleDelete(fileList[0] || { uid: '', url: imageUrl })}
            />
          )}
        </div>
      ) : editable ? (
        // No existing document yet — show the upload area (editable mode only)
        <Upload
          accept="image/*,.pdf"
          fileList={fileList}
          beforeUpload={beforeUpload}
          onChange={handleChange}
          onPreview={handlePreview}
          onRemove={handleDelete}
          listType="picture-card"
          customRequest={handleUpload}
          disabled={!editable}
          itemRender={(originNode, file) => {
            if (file.type === "application/pdf" || file.name?.endsWith(".pdf")) {
              return (
                <div style={{ textAlign: "center" }}>
                  <a
                    onClick={() => {
                      setPreviewFile(file);
                      setPreviewVisible(true);
                    }}
                  >
                    📄 {file.name || "PDF File"}
                  </a>
                </div>
              );
            }
            return originNode;
          }}
        >
          {fileList.length >= 1 ? null : uploadButton}
        </Upload>
      ) : null}


      {/* Preview Modal */}
      <Modal
        title={<span style={{ color: "white" }}>Image Preview</span>}
        visible={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        {previewFile && (
          <div style={{ position: "relative" }}>
            {(previewFile.type === "application/pdf" ||
              previewFile.name?.endsWith(".pdf") ||
              previewFile.url?.endsWith(".pdf")) ? (
              <iframe
                src={previewFile.url || URL.createObjectURL(previewFile.originFileObj)}
                title="PDF Preview"
                style={{ width: "100%", height: "500px", border: "none" }}
              />
            ) : (
              <img
                alt="preview"
                style={{ width: "100%", borderRadius: "8px" }}
                src={previewFile?.url || previewFile?.thumbUrl}
              />
            )}

            {/* Common Timestamp Overlay for both Images and PDFs */}
            {(previewFile?.timestamp || previewFile?.createdAt || previewFile?.lastModified) && (
              <div
                style={{
                  position: "absolute",
                  top: "20px",
                  right: "20px",
                  background: "rgba(0, 0, 0, 0.75)",
                  color: "white",
                  padding: "8px 16px",
                  borderRadius: "20px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  zIndex: 10,
                  backdropFilter: "blur(4px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Icon type="clock-circle" />
                <span>
                  {moment(previewFile.timestamp || previewFile.createdAt || previewFile.lastModified).isValid()
                    ? moment(previewFile.timestamp || previewFile.createdAt || previewFile.lastModified).format("DD-MM-YYYY hh:mm A")
                    : "Date Error"}
                </span>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ImageUploader;